import type { PesticideCatalogProduct, Product } from "@/type/api";
import {
	activeLifeStages,
	formatRateRange,
	formatResidualDays,
	parseLifeStageCodes,
} from "@/view/configuration/pesticideCatalog";

/**
 * DistrictProductRow is one row of the district's own product list on the
 * Products page. It carries only what the district holds and the terms it owns:
 * its name, the control class it curates, its planned residual and the life
 * stages it uses the product on. Reference facts (EPA registration, active
 * ingredients, label rates, documents) belong to the catalog page, so the two
 * pages do not render the same table.
 *
 * `filter` is the catalog-shaped product the shared search runs against — the
 * linked catalog entry with the district's own name, so search finds either
 * name.
 */
export interface DistrictProductRow {
	key: number;
	name: string;
	linkTo: string;
	/** The linked catalog entry's name, when it differs from the district's own. */
	catalogName: string | null;
	controlClass: string;
	residualInput: string;
	residualSuggested: number | null;
	targetStages: Set<string>;
	/** The stored revision relationship: true exactly when this row is behind. */
	catalogUpdateAvailable: boolean;
	/** The brand this row is, as the brand row states it; null when unlinked. */
	brand: string | null;
	catalogRevision: number | null;
	catalogRevisionCurrent: number | null;
	filter: PesticideCatalogProduct;
}

/** A district row: the district's own facts, with the linked catalog entry for search only. */
export function buildDistrictRow(product: Product): DistrictProductRow {
	const catalog = product.catalog;
	return {
		key: product.id,
		name: product.name,
		linkTo: `/_/inventory/products/${product.id}`,
		catalogName:
			catalog && catalog.name.trim() !== product.name.trim()
				? catalog.name
				: null,
		controlClass: product.control_class ?? "",
		residualInput:
			product.residual_days !== null ? String(product.residual_days) : "",
		residualSuggested: product.residual_days_suggested,
		targetStages: districtTargetStages(product),
		catalogUpdateAvailable: product.catalog_update_available,
		catalogRevision: product.catalog_revision,
		catalogRevisionCurrent: product.catalog_revision_current,
		brand: product.registry_brand_name,
		filter: catalog
			? { ...catalog, name: product.name }
			: unlinkedFilter(product),
	};
}

/**
 * CatalogProductRow is one row of the shared catalog on the Catalog page. It
 * carries the catalog's reference facts and the server-owned district-list
 * state: whether the district holds the entry, and the district product that
 * owns it so the remove action can retire that row.
 */
export interface CatalogProductRow {
	key: number;
	name: string;
	/** The catalog's stable slug, the identity the add action names. */
	productID: string;
	linkTo: string;
	category: string;
	formulation: string | null;
	manufacturer: string | null;
	activeIngredients: string | null;
	epaRegistration: string | null;
	targets: Set<string>;
	residualLabel: string;
	rateRangeLabel: string;
	labelDocumentURL: string | null;
	sdsDocumentURL: string | null;
	inDistrict: boolean;
	districtProductID: number | null;
	filter: PesticideCatalogProduct;
}

/** A catalog row: every reference column reads off the shared catalog entry. */
export function buildCatalogRow(
	product: PesticideCatalogProduct,
): CatalogProductRow {
	return {
		key: product.id,
		name: product.name,
		productID: product.product_id,
		linkTo: `/_/inventory/catalog/${product.id}`,
		category: product.category,
		formulation: product.formulation,
		manufacturer: product.manufacturer,
		activeIngredients: product.active_ingredients,
		epaRegistration: product.epa_registration,
		targets: activeLifeStages(product.target_life_stages),
		residualLabel: formatResidualDays(product.rates),
		rateRangeLabel: formatRateRange(product.rates),
		labelDocumentURL: product.label_document_url,
		sdsDocumentURL: product.sds_document_url,
		inDistrict: product.in_district,
		districtProductID: product.district_product_id,
		filter: product,
	};
}

/**
 * The life stages to show for a district product: the district's own selection
 * when it has set one, otherwise the linked catalog entry's (activeLifeStages).
 * A NULL target_life_stages reads as "inherit", not "none", so the circles are
 * prefilled from the catalog facts we already hold until the district states
 * its own.
 */
function districtTargetStages(product: Product): Set<string> {
	if (product.target_life_stages !== null) {
		return parseLifeStageCodes(product.target_life_stages);
	}
	return activeLifeStages(product.catalog?.target_life_stages);
}

/** A district product the catalog does not carry still lists, with a bare filter. */
function unlinkedFilter(product: Product): PesticideCatalogProduct {
	return {
		id: product.id,
		product_id: product.product_catalog_id ?? "",
		name: product.name,
		category: "",
		formulation: null,
		manufacturer: null,
		active_ingredients: null,
		ai_composition_source: null,
		epa_registration: null,
		epa_source: null,
		dpr_registration_number: null,
		target_life_stages: null,
		label_file: null,
		sds_file: null,
		label_url: null,
		label_source: null,
		formulation_source: null,
		formulation_source_note: null,
		manufacturer_source: null,
		manufacturer_source_note: null,
		created: "",
		retired: null,
		label_document_url: null,
		sds_document_url: null,
		label_source_url: null,
		label_retrieved_at: null,
		sds_source_url: null,
		sds_retrieved_at: null,
		in_district: false,
		district_product_id: null,
		rates: [],
		revision: 0,
		registry_product_status: null,
		label_accepted_date: null,
		pending_change_count: 0,
		registry_brand_id: null,
		registry_brand_name: null,
		registry_brand_active: null,
		registry_brand_sds_present: null,
		registry_brand_sds_document_url: null,
	};
}
